import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { MoveHorizontal, MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const imgUrl = (u) => (u && u.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

const FALLBACK = [
  {
    id: "tegole",
    title: "Rifacimento copertura in coppi e tegole portoghesi",
    location: "Abbiategrasso (MI)",
    desc: "Rimozione del vecchio manto deteriorato, nuova guaina di sottocolmo e posa a regola d'arte di tegole in terracotta.",
    before_url: "https://images.unsplash.com/photo-1564783679669-f5391270417b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHxfHxvbGQlMjBkYW1hZ2VkJTIwcm9vZiUyMG1vc3MlMjB0aWxlc3xlbnwwfHx8fDE3OTI3OTExOTB8MA&ixlib=rb-4.1.0&q=85",
    after_url: "https://images.unsplash.com/photo-1610056868457-e61d6f9eeb86?crop=entropy&cs=srgb&fm=jpg&ixid=M3w8NjA1NDh8MHwxfHNlYXJjaHw0fHxuZXclMjB0ZXJyYWNvdHRhJTIwdGlsZSUyMHJvb2YlMjBob3VzZXxlbnwwfHx8fDE3OTI3OTExOTB8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    id: "guaina",
    title: "Impermeabilizzazione lastrico solare in guaina",
    location: "Milano",
    desc: "Bonifica delle infiltrazioni su copertura piana e posa di doppio strato di membrana armata con finitura protettiva.",
    before_url: "https://images.unsplash.com/photo-1678020706579-de8ce9e33ca8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHw0fHxvbGQlMjBkYW1hZ2VkJTIwcm9vZiUyMG1vc3MlMjB0aWxlc3xlbnwwfHx8fDE3OTI3OTExOTB8MA&ixlib=rb-4.1.0&q=85",
    after_url: "https://images.unsplash.com/photo-1601277142977-2bd75b6b33be?crop=entropy&cs=srgb&fm=jpg&ixid=M3w8NjAzMzl8MHwxfHNlYXJjaHwyfHxkYXJrJTIwYXJjaGl0ZWN0dXJlJTIwcm9vZiUyMHRpbGVzJTIwcm9vZmVycyUyMGNyYWZ0c21hbnNoaXAlMjBjb3BwZXIlMjByb29mfGVufDB8fHxibGFja3wxNzkyNzkwMDYyfDA&ixlib=rb-4.1.0&q=85",
  },
  {
    id: "finto-coppo",
    title: "Riqualificazione con pannelli finto coppo coibentati",
    location: "Vigevano (PV)",
    desc: "Sostituzione della copertura degradata con pannelli sandwich isolanti da 80mm, finitura effetto coppo rosso rame.",
    before_url: "https://images.unsplash.com/photo-1721493707262-0fc9e5794c27?crop=entropy&cs=srgb&fm=jpg&ixid=M3w8NjY2NzN8MHwxfHNlYXJjaHzfHxvbGQlMjBkYW1hZ2VkJTIwcm9vZiUyMG1vc3MlMjB0aWxlc3xlbnwwfHx8fDE3OTI3OTExOTB8MA&ixlib=rb-4.1.0&q=85",
    after_url: "https://images.unsplash.com/photo-1579688212554-9efca19e9ed8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHw0fHxkYXJrJTIwYXJjaGl0ZWN0dXJlJTIwcm9vZiUyMHRpbGVzJTIwcm9vZmVycyUyMGNyYWZ0c21hbnNoaXAlMjBjb3BwZXIlMjByb29mfGVufDB8fHxibGFja3wxNzkyNzkwMDYyfDA&ixlib=rb-4.1.0&q=85",
  },
  {
    id: "lattoneria",
    title: "Grondaie e lattoneria di pregio in rame",
    location: "Pavia (PV)",
    desc: "Sostituzione completa dei canali ammalorati con pluviali in rame sagomato, converse e scossaline a tenuta.",
    before_url: "https://images.unsplash.com/photo-1628525901421-5cd4266b2e69?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwyfHxjb3BwZXIlMjBndXR0ZXIlMjByb29mJTIwYnVpbGRpbmd8ZW58MHx8fHwxNzkyNzkxMTk0fDA&ixlib=rb-4.1.0&q=85",
    after_url: "https://images.unsplash.com/photo-1767714453108-6655d64aa3b7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxjb3BwZXIlMjBndXR0ZXIlMjByb29mJTIwYnVpbGRpbmd8ZW58MHx8fHwxNzkyNzkxMTk0fDA&ixlib=rb-4.1.0&q=85",
  },
];

const BeforeAfter = ({ project, index }) => {
  const ref = useRef(null);
  const dragging = useRef(false);
  const [pos, setPos] = useState(50);

  const update = (clientX) => {
    const r = ref.current.getBoundingClientRect();
    setPos(Math.min(94, Math.max(6, ((clientX - r.left) / r.width) * 100)));
  };

  return (
    <motion.article
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, delay: (index % 2) * 0.12, ease: [0.22, 1, 0.36, 1] }}
      className="rounded-3xl overflow-hidden bg-[#14171E] border border-white/10 hover:border-[#D97736]/40 transition-colors duration-500"
    >
      <div
        ref={ref}
        className="relative h-60 sm:h-72 overflow-hidden cursor-ew-resize select-none touch-none"
        onPointerDown={(e) => {
          dragging.current = true;
          e.currentTarget.setPointerCapture(e.pointerId);
          update(e.clientX);
        }}
        onPointerMove={(e) => dragging.current && update(e.clientX)}
        onPointerUp={() => (dragging.current = false)}
        onPointerCancel={() => (dragging.current = false)}
      >
        <img
          src={imgUrl(project.after_url)}
          alt={`${project.title} — dopo l'intervento`}
          data-testid={`gallery-project-${project.id}-after`}
          loading="lazy"
          draggable={false}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <img
          src={imgUrl(project.before_url)}
          alt={`${project.title} — prima dell'intervento`}
          data-testid={`gallery-project-${project.id}-before`}
          loading="lazy"
          draggable={false}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
        />
        <span className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-[0.25em] bg-[#0B0C0E]/70 backdrop-blur-md border border-white/10 rounded-full px-3 py-1.5 text-[#F3F4F6]">
          Prima
        </span>
        <span className="absolute top-4 right-4 font-mono text-[10px] uppercase tracking-[0.25em] bg-[#D97736]/85 rounded-full px-3 py-1.5 text-[#0B0C0E] font-medium">
          Dopo
        </span>
        <div className="absolute inset-y-0" style={{ left: `${pos}%` }} aria-hidden="true">
          <div className="absolute inset-y-0 -translate-x-1/2 w-[2px] bg-[#D97736]" />
          <div className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-[#D97736] text-[#0B0C0E] flex items-center justify-center shadow-[0_8px_24px_rgba(217,119,54,0.45)]">
            <MoveHorizontal size={17} />
          </div>
        </div>
      </div>
      <div className="p-6">
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#D97736] mb-2">
          <MapPin size={12} />
          {project.location}
        </div>
        <h3 className="font-syne font-semibold text-lg mb-2">{project.title}</h3>
        <p className="text-sm text-[#9CA3AF] leading-relaxed">{project.desc}</p>
      </div>
    </motion.article>
  );
};

export default function Gallery() {
  const [projects, setProjects] = useState(FALLBACK);

  useEffect(() => {
    axios
      .get(`${API}/galleria`)
      .then((r) => {
        if (Array.isArray(r.data) && r.data.length > 0) setProjects(r.data);
      })
      .catch(() => {});
  }, []);

  return (
    <section id="galleria" className="py-24 sm:py-32 bg-[#13151A] border-y border-white/5" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mb-14 max-w-3xl"
        >
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#D97736] mb-4">I nostri lavori</p>
          <h2 className="font-syne font-bold tracking-tight leading-tight text-3xl sm:text-4xl lg:text-5xl">
            Prima e dopo: la differenza si vede dal tetto.
          </h2>
          <p className="mt-4 text-base text-[#9CA3AF]">
            Trascina il cursore sulle foto per confrontare ogni intervento.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {projects.map((p, i) => (
            <BeforeAfter key={p.id} project={p} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
